import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, firstValueFrom, map } from 'rxjs';
import { InstanceConfig, InstanceService, InstanceBranding, InstanceConfigInmateVisibility } from '@tylertech/cj-inmate-search-angular-sdk';
import { InstanceManagerService } from './instance-manager.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

// Cache interfaces for type safety
interface CacheEntry {
  value: InstanceConfig;
  expiry: number;
}

interface CacheStore {
  version: number;
  // Map from instanceId to cache entry
  entries: Record<string, CacheEntry>;
  // Map from alias to instanceId for quick lookup
  aliasMap: Record<string, string>;
}

interface CacheOptions {
  ttl?: number;
  skipCache?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class InstanceConfigService implements OnDestroy {
  // Constants
  private readonly CACHE_VERSION = 1;
  private readonly CACHE_KEY = 'instance-configs';
  private readonly DEFAULT_CACHE_TTL = 1000 * 60 * 60 * 24; // 24 hours

  // Private subjects
  private readonly configSubject = new BehaviorSubject<InstanceConfig | null>(null);
  private readonly subscriptions: Subscription[] = [];
  private alias: string | null = null;
  private currentInstanceId: string | null = null;
  private hasLocalStorage: boolean;

  // Public observables
  readonly config$ = this.configSubject.asObservable();
  readonly branding$ = this.config$.pipe(
    map(config => config?.branding ?? null)
  );
  readonly siteTitle$ = this.branding$.pipe(
    map(branding => branding?.siteTitle)
  );
  readonly logo$ = this.branding$.pipe(
    map(branding => branding?.logo)
  );
  readonly alias$ = this.config$.pipe(
    map(config => config?.alias ?? null)
  );
  readonly inmateVisibility$ = this.config$.pipe(
    map(config => config?.inmateVisibility ?? null)
  );

  // Snapshot getters
  get configSnapshot(): InstanceConfig | null {
    return this.configSubject.getValue();
  }

  get brandingSnapshot(): InstanceBranding | null {
    return this.configSnapshot?.branding ?? null;
  }

  get inmateVisibilitySnapshot(): InstanceConfigInmateVisibility | null {
    return this.configSnapshot?.inmateVisibility ?? null;
  }

  constructor(
    private readonly _instanceService: InstanceService,
    private readonly _instanceManagerService: InstanceManagerService,
    private readonly _router: Router
  ) {
    // Check if localStorage is available
    this.hasLocalStorage = this.checkLocalStorage();
    this.initializeCache();
    this.loadFromCache();
    this.initializeSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async refreshConfig(options?: CacheOptions): Promise<void> {
    await this.loadConfig(options);
  }

  public clearConfigCache(): void {
    // Clear only the current instance from cache
    if (this.currentInstanceId) {
      this.removeFromCache(this.currentInstanceId);
    }
    this.configSubject.next(null);
  }

  public clearAllConfigCache(): void {
    // Clear all cached data
    this.resetCache();
    this.configSubject.next(null);
  }

  // Private methods
  private initializeSubscriptions(): void {
    // Route change subscription
    this.subscriptions.push(
      this._router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        this.handleRouteChange();
      })
    );

    // Instance change subscription
    this.subscriptions.push(
      this._instanceManagerService.instance$.subscribe(() => {
        this.handleInstanceChange();
      })
    );
  }

  private async handleRouteChange(): Promise<void> {
    const newAlias = this.findAliasInRoute(this._router.routerState.root);
    if (newAlias !== this.alias) {
      this.alias = newAlias;
      this.currentInstanceId = null;
      this.configSubject.next(null);

      // Try to get instanceId from alias map
      const instanceId = this.getInstanceIdFromAlias(this.alias);
      if (instanceId) {
        this.currentInstanceId = instanceId;
        if (this.loadFromCache()) {
          return;
        }
      }

      if (this.alias) {
        await this.loadConfig().catch(err => this.handleError(err));
      }
    }
  }

  private async handleInstanceChange(): Promise<void> {
    const instanceId = this._instanceManagerService.instanceIdSnapshot;
    if (instanceId) {
      this.currentInstanceId = instanceId;
      if (this.loadFromCache()) {
        return;
      }
    }
    await this.loadConfig().catch(err => this.handleError(err));
  }

  private async loadConfig(options?: CacheOptions): Promise<void> {
    const targetInstanceId = this.currentInstanceId;
    try {
      // Skip cache if requested
      if (options?.skipCache && this.currentInstanceId) {
        this.removeFromCache(this.currentInstanceId);
      }

      const config = await this.fetchConfig();
      if (config) {
        // Update the current instanceId if it was resolved from an alias
        this.currentInstanceId = config.instanceId;
        this.configSubject.next(config);
        this.saveToCache(config, options?.ttl);
      } else {
        this.configSubject.next(null);
      }
    } catch (error) {
      const isCurrentInstanceRefresh = targetInstanceId && targetInstanceId === this.currentInstanceId && this.configSubject.getValue()?.instanceId === targetInstanceId;
      if (!isCurrentInstanceRefresh) {
        this.configSubject.next(null);
      }
    }
  }

  private async fetchConfig(): Promise<InstanceConfig | null> {
    try {
      // Try alias lookup first
      if (this.alias) {
        const instance = await firstValueFrom(
          this._instanceService.instanceLookup({ alias: this.alias })
        );

        // Store the alias to instanceId mapping
        if (instance?.instanceId) {
          this.mapAliasToInstanceId(this.alias, instance.instanceId);
        }

        return instance;
      }

      // Try admin path using instance ID
      if (this.currentInstanceId) {
        return await firstValueFrom(
          this._instanceService.getInstanceConfig(this.currentInstanceId)
        );
      }
    } catch (error: any) {
      this.handleError(error);
      if (error?.status === 404) {
        return null;
      }
      throw error;
    }

    return null;
  }

  private findAliasInRoute(route: any): string | null {
    if (route.firstChild) {
      return this.findAliasInRoute(route.firstChild);
    }
    return route.snapshot.params.alias ?? null;
  }

  private handleError(error: unknown): void {
    console.error('Error in InstanceConfigService:', error);
  }

  // Cache Methods
  private initializeCache(): void {
    if (!this.hasLocalStorage) return;

    try {
      // Initialize cache if not exist
      if (!localStorage.getItem(this.CACHE_KEY)) {
        const emptyStore: CacheStore = {
          version: this.CACHE_VERSION,
          entries: {},
          aliasMap: {}
        };
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(emptyStore));
      }

      // Clean up expired entries
      this.cleanupExpiredEntries();
    } catch (error) {
      console.warn('Error initializing cache:', error);
      this.resetCache();
    }
  }

  private getCache(): CacheStore {
    if (!this.hasLocalStorage) {
      return { version: this.CACHE_VERSION, entries: {}, aliasMap: {} };
    }

    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      if (!cacheData) {
        return { version: this.CACHE_VERSION, entries: {}, aliasMap: {} };
      }

      const cache = JSON.parse(cacheData) as CacheStore;

      // Handle version mismatch
      if (cache.version !== this.CACHE_VERSION) {
        this.resetCache();
        return { version: this.CACHE_VERSION, entries: {}, aliasMap: {} };
      }

      return cache;
    } catch (error) {
      console.warn('Error getting cache:', error);
      this.resetCache();
      return { version: this.CACHE_VERSION, entries: {}, aliasMap: {} };
    }
  }

  private saveCache(cache: CacheStore): void {
    if (!this.hasLocalStorage) return;

    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('Error saving cache:', error);

      // If quota exceeded, remove older entries and try again
      if (error instanceof DOMException && (error.name === 'QuotaExceededError')) {
        this.pruneCache();
        try {
          localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
        } catch (retryError) {
          console.warn('Failed to save cache after pruning:', retryError);
          this.resetCache();
        }
      }
    }
  }

  private resetCache(): void {
    if (!this.hasLocalStorage) return;

    try {
      const emptyStore: CacheStore = {
        version: this.CACHE_VERSION,
        entries: {},
        aliasMap: {}
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(emptyStore));
    } catch (error) {
      console.warn('Error resetting cache:', error);
    }
  }

  private loadFromCache(): boolean {
    if (!this.hasLocalStorage || !this.currentInstanceId) return false;

    try {
      const cache = this.getCache();
      const entry = cache.entries[this.currentInstanceId];

      // Check if entry exists and is not expired
      if (entry && entry.expiry > Date.now()) {
        this.configSubject.next(entry.value);

        // Schedule a background refresh if the entry is more than 5 minutes old
        const fiveMinutes = 5 * 60 * 1000;
        const entryAge = Date.now() - (entry.expiry - this.DEFAULT_CACHE_TTL);

        if (entryAge > fiveMinutes) {
          // Use setTimeout to make this non-blocking
          setTimeout(() => {
            this.refreshInBackground();
          }, 0);
        }

        return true;
      }

      // Remove expired entry
      if (entry) {
        this.removeFromCache(this.currentInstanceId);
      }
    } catch (error) {
      console.warn('Error loading from cache:', error);
    }

    return false;
  }

  // Add a new method for background refresh
  private async refreshInBackground(): Promise<void> {
    try {
      if (!this.currentInstanceId) return;

      // Silently refresh without affecting current UI state
      const config = await this.fetchConfig();

      if (config) {
        // Only update UI if the config has actually changed
        const currentConfig = this.configSubject.getValue();

        // Deep comparison could be added here for more complex scenarios
        const hasChanged = JSON.stringify(config) !== JSON.stringify(currentConfig);

        if (hasChanged) {
          this.configSubject.next(config);
        }

        // Always update the cache
        this.saveToCache(config);
      }
    } catch (error) {
      // Just log the error, don't disrupt the UI
      console.warn('Background refresh failed:', error);
    }
  }

  private saveToCache(config: InstanceConfig, ttl?: number): void {
    if (!this.hasLocalStorage || !config?.instanceId) return;

    try {
      const cache = this.getCache();

      // Add or update instance config
      cache.entries[config.instanceId] = {
        value: config,
        expiry: Date.now() + (ttl ?? this.DEFAULT_CACHE_TTL)
      };

      // Update alias mapping if alias exists
      if (config.alias) {
        cache.aliasMap[config.alias] = config.instanceId;
      }

      this.saveCache(cache);
    } catch (error) {
      console.warn('Error saving to cache:', error);
    }
  }

  private removeFromCache(instanceId: string): void {
    if (!this.hasLocalStorage) return;

    try {
      const cache = this.getCache();

      // Remove the instance entry
      if (cache.entries[instanceId]) {
        const alias = cache.entries[instanceId].value.alias;
        delete cache.entries[instanceId];

        // Remove any alias mappings pointing to this instance
        Object.keys(cache.aliasMap).forEach(key => {
          if (cache.aliasMap[key] === instanceId) {
            delete cache.aliasMap[key];
          }
        });

        this.saveCache(cache);
      }
    } catch (error) {
      console.warn('Error removing from cache:', error);
    }
  }

  private cleanupExpiredEntries(): void {
    if (!this.hasLocalStorage) return;

    try {
      const cache = this.getCache();
      let hasChanges = false;

      // Remove expired entries
      Object.keys(cache.entries).forEach(instanceId => {
        if (cache.entries[instanceId].expiry < Date.now()) {
          delete cache.entries[instanceId];
          hasChanges = true;
        }
      });

      // Clean up alias map entries with no corresponding instances
      Object.keys(cache.aliasMap).forEach(alias => {
        const instanceId = cache.aliasMap[alias];
        if (!cache.entries[instanceId]) {
          delete cache.aliasMap[alias];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.saveCache(cache);
      }
    } catch (error) {
      console.warn('Error cleaning up expired entries:', error);
    }
  }

  private pruneCache(): void {
    if (!this.hasLocalStorage) return;

    try {
      const cache = this.getCache();

      // Get entries sorted by expiry (oldest first)
      const sortedEntries = Object.entries(cache.entries)
        .sort((a, b) => a[1].expiry - b[1].expiry);

      // Remove oldest half of the entries
      if (sortedEntries.length > 1) {
        const entriesToRemove = Math.ceil(sortedEntries.length / 2);
        for (let i = 0; i < entriesToRemove; i++) {
          const [instanceId] = sortedEntries[i];
          delete cache.entries[instanceId];
        }

        // Clean up alias map
        Object.keys(cache.aliasMap).forEach(alias => {
          const instanceId = cache.aliasMap[alias];
          if (!cache.entries[instanceId]) {
            delete cache.aliasMap[alias];
          }
        });

        this.saveCache(cache);
      }
    } catch (error) {
      console.warn('Error pruning cache:', error);
      this.resetCache();
    }
  }

  private getInstanceIdFromAlias(alias: string | null): string | null {
    if (!this.hasLocalStorage || !alias) return null;

    try {
      const cache = this.getCache();
      return cache.aliasMap[alias] || null;
    } catch (error) {
      console.warn('Error getting instance ID from alias:', error);
      return null;
    }
  }

  private mapAliasToInstanceId(alias: string, instanceId: string): void {
    if (!this.hasLocalStorage || !alias || !instanceId) return;

    try {
      const cache = this.getCache();
      cache.aliasMap[alias] = instanceId;
      this.saveCache(cache);
    } catch (error) {
      console.warn('Error mapping alias to instance ID:', error);
    }
  }

  private checkLocalStorage(): boolean {
    try {
      const testKey = '__test_storage__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }
}